import { ReactNode } from 'react';
import axios, { type AxiosError, AxiosRequestConfig } from 'axios';
import useSWR, { type Fetcher, Middleware, mutate, SWRConfig, type SWRConfiguration, SWRHook, useSWRConfig } from 'swr';
import useSWRMutation, { type MutationFetcher } from 'swr/mutation';
import useSWRImmutable from 'swr/immutable';
import useSWRInfinite, { type SWRInfiniteConfiguration } from 'swr/infinite';
import useSWRSubscription, { type SWRSubscription } from 'swr/subscription';
import { useUser } from 'services/User';

export interface AxiosConfig extends Partial<AxiosRequestConfig> {
    url: string;
    baseURL?: string;
    headers?: {
        apikey?: string;
        'X-Csrf'?: string;
        engine_names?: string;
    };
    dataId?: string;
}

export type SWRKey = AxiosConfig | null;

interface SWRProviderProps {
    children: ReactNode;
}

const login: Middleware = (useSWRNext: SWRHook) => (key, fetcher, config) => {
    const { user } = useUser();

    // Skip the login middleware for subscriptions
    if ((key as SWRKey)?.url?.includes('wss://')) {
        return useSWRNext(key, fetcher, config);
    }

    const baseURL = process.env.GATSBY_MDC_PLATFORM_URL;

    if (!fetcher) {
        throw new Error('SWR requires a fetcher function');
    }
    if (!baseURL) {
        throw new Error('SWR requires a default baseURL');
    }

    const loginFetcher: MutationFetcher<any, SWRKey> = async (reqConfig, options) => {
        const apikey = user?.portal_api_key ?? user?.apikey;
        const loginConfig = {
            baseURL,
            ...reqConfig,
            headers: {
                apikey,
                ...reqConfig?.headers,
            },
        };

        return fetcher(loginConfig, options);
    };

    return useSWRNext(key, loginFetcher, config);
};

export const fetcher: Fetcher<any, SWRKey> = async (config) => axios({ method: 'GET', ...config }).then(res => res.data);

export const SWRProvider = ({ children }: SWRProviderProps) => {
    return (
        <SWRConfig
            value={{
                fetcher,
                use: [login],
                errorRetryCount: 2,
            }}
        >
            {children}
        </SWRConfig>
    );
};

export const clearCache = () => mutate(() => true, undefined, { revalidate: false });

export {
    axios,
    useSWR,
    useSWRConfig,
    useSWRMutation,
    useSWRImmutable,
    useSWRInfinite,
    useSWRSubscription,
    type AxiosError,
    type Fetcher,
    type MutationFetcher,
    type SWRConfiguration,
    type SWRInfiniteConfiguration,
    type SWRSubscription,
};
